import classNames from "classnames";
import React, { Component } from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import { CaptionBolder, SmallBody, SmallBodyBolder } from "src/components/Text";
import { localize } from "src/l10n";
import { debounce } from "src/utils";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { Label, Scrollable } from ".";
import { Style } from "../helpers";
import { UnstyledButton } from "./Buttons";
import "./TableOfContent.scss";

interface IProps {
    tableOfContentSearchElementRef: any;
    isSmallViewMode: boolean;
    /** Stays in view while scrolling (CSS sticky) and marks the heading in view. */
    isSticky?: boolean;
    /** Collapsed behind "Innehållsförteckning ▾" (phones). */
    isCollapsible?: boolean;
}

interface IState {
    isLoading: boolean;
    headers: any[];
    fixedMode: boolean;
    marginTop: number;
    activeIndex: number;
    isOpen: boolean;
}

// A heading counts as "in view" once it has passed this far down from the top
const activeOffset = 140;

class TableOfContent extends Component<IProps, IState> {
    private headerInterval: ReturnType<typeof setInterval> | undefined;
    private retryTimeout: ReturnType<typeof setTimeout> | undefined;

    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
            headers: [],
            fixedMode: false,
            marginTop: 0,
            activeIndex: 0,
            isOpen: false
        };
    }

    getHeaders() {
        const wrapper = ReactDOM.findDOMNode(this.props.tableOfContentSearchElementRef.current);

        if (!wrapper) {
            return;
        }

        // @ts-ignore
        const headerElements = wrapper.querySelectorAll('h1, h2, h3, h4, h5, h6');

        const headers = [];

        for (const h of headerElements) {
            const text = h.innerText.replace(/(\r\n|\n|\r)/gm, '').trim();

            if (!text) {
                continue;
            }

            headers.push({
                text,
                el: h
            });
        }

        this.setState({
            isLoading: false,
            headers
        });
    }

    listenToScroll() {
        const winScroll = document.body.scrollTop ||
            document.documentElement.scrollTop;

        if (this.props.isSticky) {
            let activeIndex = 0;

            this.state.headers.forEach((header, index) => {
                if (header.el.getBoundingClientRect().top <= activeOffset) {
                    activeIndex = index;
                }
            });

            this.setState({ activeIndex });

            return;
        }

        const shouldBeFixedMode = winScroll > 300;

        this.setState({
            fixedMode: shouldBeFixedMode,
            marginTop: Math.max(0, winScroll - 200)
        });
    }

    debouncedListenToScroll = debounce(this.listenToScroll.bind(this), 50);

    componentDidMount() {
        this.getHeaders();

        // Rendered above the content (phones), the content's ref is set right after this mount
        if (!this.props.tableOfContentSearchElementRef.current) {
            this.retryTimeout = setTimeout(() => this.getHeaders(), 0);
        }

        this.headerInterval = setInterval(() => {
            if (!this ||
                !this.state) {
                return;
            }

            this.getHeaders();
        }, 5000);

        window.addEventListener('scroll', this.debouncedListenToScroll);
    }

    componentWillUnmount() {
        clearInterval(this.headerInterval);
        clearTimeout(this.retryTimeout);
        window.removeEventListener('scroll', this.debouncedListenToScroll);
    }

    scrollToHeader(header) {
        window.scrollTo({
            top: header.el.getBoundingClientRect().top + window.scrollY - (this.props.isSticky ? activeOffset - 20 : 0),
            left: 0,
            behavior: 'smooth'
        });
    }

    renderCollapsible() {
        // A single heading needs no table of contents
        if (this.state.headers.length < 2) {
            return null;
        }

        const { isOpen } = this.state;
        const listId = "TableOfContent-list-collapsible";

        return (
            <nav className="TableOfContent TableOfContent-collapsible" aria-label={localize("Innehallsforteckning")}>
                <button
                    type="button"
                    className="TableOfContent-toggle"
                    aria-expanded={isOpen}
                    aria-controls={listId}
                    onClick={() => this.setState({ isOpen: !isOpen })}>
                    <SmallBodyBolder as="span" weight="semiBold" color="contentDark">
                        {localize("Innehallsforteckning")}
                    </SmallBodyBolder>
                    <Visage2Icon icon="arrow-right-s-line" type="custom" color="dark-grey" rotation={isOpen ? 270 : 90} />
                </button>
                {isOpen && (
                    <ul className="TableOfContent-list" id={listId}>
                        {this.state.headers.map((header, index) => (
                            <li key={index} className="TableOfContent-entry">
                                <UnstyledButton onClick={() => {
                                    this.setState({ isOpen: false });
                                    this.scrollToHeader(header);
                                }}>
                                    <SmallBody as="span" letterSpacing="normal" color="contentNormal">
                                        {header.text}
                                    </SmallBody>
                                </UnstyledButton>
                            </li>
                        ))}
                    </ul>
                )}
            </nav>
        );
    }

    renderSticky() {
        // A single heading needs no table of contents
        if (this.state.headers.length < 2) {
            return null;
        }

        const title = localize("Innehallsforteckning");

        return (
            <nav className="TableOfContent TableOfContent-sticky" aria-label={title}>
                <CaptionBolder as="div" weight="semiBold" color="contentNormal" className="TableOfContent-title">
                    {title}
                </CaptionBolder>
                <ul className="TableOfContent-list">
                    {this.state.headers.map((header, index) => {
                        const isActive = index === this.state.activeIndex;

                        return (
                            <li key={index} className={classNames("TableOfContent-entry", { active: isActive })}>
                                <UnstyledButton
                                    onClick={() => this.scrollToHeader(header)}
                                    handleProps={isActive ? { "aria-current": "location" } : undefined}>
                                    <SmallBody
                                        as="span"
                                        letterSpacing="normal"
                                        weight={isActive ? "semiBold" : "regular"}
                                        color={isActive ? "contentDark" : "contentNormal"}>
                                        {header.text}
                                    </SmallBody>
                                </UnstyledButton>
                            </li>
                        );
                    })}
                </ul>
            </nav>
        );
    }

    render() {
        if (this.state.isLoading ||
            !this.state.headers ||
            this.state.headers.length === 0) {
            return null;
        }

        if (this.props.isCollapsible) {
            return this.renderCollapsible();
        }

        if (this.props.isSticky) {
            return this.renderSticky();
        }

        return (
            <div style={{
                position: "relative"
            }}>
                <div className="TableOfContent" style={{
                    width: "100%",
                    position: this.state.fixedMode ? "absolute" : "relative",
                    marginTop: this.state.fixedMode ? this.state.marginTop : 0,
                    transition: "margin-top 0.25s linear"
                }}>
                    <Label
                        size="body-2"
                        color="mid-grey"
                        className="header"
                        style={{
                            marginBottom: Style.getSpacingStr(2),
                            overflowWrap: "normal",
                            wordBreak: "normal",
                        }}
                    >
                        {
                            localize("Innehallsforteckning")
                        }
                    </Label>
                    <div
                        className="TableOfContent-items"
                        style={{
                            ...(!this.props.isSmallViewMode ? { height: 300, minWidth: 250 } : {}),
                        }}
                    >
                        <Scrollable>
                            <div>
                                {
                                    this.state.headers.map((header, index) => {
                                        return (
                                            <div key={index} >
                                                <UnstyledButton onClick={() => this.scrollToHeader(header)}>
                                                    <Label size="body-2" color="light-blue">
                                                        {
                                                            header.text
                                                        }
                                                    </Label>
                                                </UnstyledButton>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        </Scrollable>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: Spintr.AppState, props) => {
    return {
        ...props,
        isSmallViewMode: state.ui.isSmallViewMode,
    };
};

export default connect(mapStateToProps)(TableOfContent);
